import { createStore, applyMiddleware, Middleware } from '@reduxjs/toolkit';
// eslint-disable-next-line import/no-extraneous-dependencies
import { composeWithDevTools } from '@redux-devtools/extension';
// eslint-disable-next-line import/no-extraneous-dependencies
import { thunk } from 'redux-thunk';
import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';

import { historicalScenariosApi } from './api/historical-scenarios/historical-scenarios-api';
import { planningPlanAccountsApi } from './api/planning/planning-api';
import { aiStatementReaderApi } from './api/ai-statement-reader';
import { screenerApi } from './api/screener';

import createRootReducer from './root-reducer';
import LogRocketUtils from '../utils/3rd-party/logrocketUtils';
import { ReduxState } from './modules/types';

export const history = createBrowserHistory({
  basename: window.reactBaseName !== '{{REACT_BASE_PATH}}' ? window.reactBaseName : undefined,
});

const rootReducer = createRootReducer(history);

const middlewares: Middleware[] = [
  thunk,
  routerMiddleware(history),
  historicalScenariosApi.middleware,
  planningPlanAccountsApi.middleware,
  aiStatementReaderApi.middleware,
  screenerApi.middleware,
];

// LogRocket has to be the last middleware in the list
middlewares.push(LogRocketUtils.getReduxMiddleware());

const devToolsOptions = {
  stateSanitizer: <S>(state: S) => {
    const reduxState = state as ReduxState;

    // Redact intl state to avoid large state in devtools
    if (reduxState?.intl) {
      return { ...reduxState, intl: '<<JSON_DATA>>' } as S;
    }

    return state;
  },
  actionSanitizer: <A>(action: A) => action,
};

export const createTestStore = (initialState = {}) =>
  createStore(rootReducer, initialState as ReduxState, composeWithDevTools(devToolsOptions)(applyMiddleware(...middlewares)));

const store = createStore(rootReducer, {} as ReduxState, composeWithDevTools(devToolsOptions)(applyMiddleware(...middlewares)));

if (typeof window !== 'undefined') {
  // Only expose in development/staging environments for security
  const shouldExposeStore = import.meta.env.REACT_APP_DEBUG_REDUX_STORE === 'true';

  if (shouldExposeStore) {
    (window as typeof window & { store: typeof store }).store = store;
    console.log('🔧 Redux store exposed globally for testing');
  }
}

export default store;
